import type { HocuspocusProvider } from "@hocuspocus/provider";
import type { CollaborationOptions } from "@tiptap/extension-collaboration";
import type { CollaborationCaretOptions } from "@tiptap/extension-collaboration-caret";
import type { Attrs, Node as PMNode } from "@tiptap/pm/model";
import type { Transaction } from "@tiptap/pm/state";
import { combineTransactionSteps, Extension, findChildren, findChildrenInRange, getChangedRanges, isNodeSelection } from "@tiptap/core";
import { Fragment, Slice } from "@tiptap/pm/model";
import { NodeSelection, Plugin, PluginKey } from "@tiptap/pm/state";
import { createEventHook } from "@vueuse/core";
import { findDuplicates } from "@/common/lib/array";
import { newGuid } from "@/common/lib/string";

/**
 * Code from Tiptap UniqueID extension (https://tiptap.dev/api/extensions/unique-id)
 */
export type UniqueIDAttribute = "unique-id";
export const UniqueIDAttributeName: UniqueIDAttribute = "unique-id";
export interface UniqueIDAttributes {
  [UniqueIDAttributeName]: string;
}
export const UniqueIDDataAttributeName = `data-${UniqueIDAttributeName}` as const;

interface UniqueIDOptions {
  types: string[];
  generateID: () => string;
  filterTransaction?: (transaction: Transaction) => boolean;
}

export function isUniqueIdNode(node: PMNode): node is PMNode & { attrs: Record<UniqueIDAttribute, string> } {
  return !!node.attrs[UniqueIDAttributeName];
}

export function getNodeUniqueId(node: PMNode): string | undefined {
  if (!isUniqueIdNode(node)) return undefined;
  return node.attrs[UniqueIDAttributeName];
}

export function getAttrsUniqueId(attrs: Attrs): string | undefined {
  return attrs[UniqueIDAttributeName] as string | undefined;
}

export const documentNodeAdded = createEventHook<string>();

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    uniqueId: {
      /**
       * Add unique id to node.
       */
      ensureUniqueId: (node: PMNode, pos: number) => ReturnType;
    };
  }
}

export const UniqueIDPluginKey = new PluginKey("uniqueID");
const UniqueID = Extension.create<UniqueIDOptions>({
  name: "uniqueID",
  // we’ll set a very high priority to make sure this runs first
  // and is compatible with `appendTransaction` hooks of other extensions
  priority: 10000,
  addOptions() {
    return {
      types: [],
      generateID: newGuid,
      filterTransaction: undefined,
    };
  },
  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          [UniqueIDAttributeName]: {
            default: null,
            parseHTML: element => element.getAttribute(`data-${UniqueIDAttributeName}`),
            renderHTML: attributes => ({
              [`data-${UniqueIDAttributeName}`]: (attributes as Record<string, string>)[UniqueIDAttributeName],
            }),
          },
        },
      },
    ];
  },

  onCreate() {
    // check initial content for missing ids
    const collaboration = this.editor.extensionManager.extensions.find(ext => ext.name === "collaboration") as Extension<CollaborationOptions> | undefined;
    const collaborationCaret = this.editor.extensionManager.extensions.find(ext => ext.name === "collaborationCaret") as Extension<CollaborationCaretOptions> | undefined;
    const provider: HocuspocusProvider | undefined = collaboration?.options?.provider ?? collaborationCaret?.options?.provider;
    if (!provider) {
      console.warn("UniqueID extension expected collaboration provider but it wasn't found.");
    }

    const assignUniqueIDs = () => {
      const { view, state } = this.editor;
      const { tr, doc } = state;
      const { types, generateID } = this.options;

      const nodesWithoutId = findChildren(doc, node => {
        return (
          types.includes(node.type.name) && node.attrs[UniqueIDAttributeName] === null
        );
      });
      nodesWithoutId.forEach(({ node, pos }) => {
        tr.setNodeMarkup(pos, undefined, {
          ...node.attrs,
          [UniqueIDAttributeName]: generateID(),
        });
      });

      tr.setMeta("addToHistory", false);
      view.dispatch(tr);

      provider && provider.off("synced", assignUniqueIDs);
    };
    if (provider) {
      provider.on("synced", assignUniqueIDs);
    } else {
      assignUniqueIDs();
    }
  },

  addCommands() {
    return {
      ensureUniqueId: (node, pos) => ({ tr }) => {
        const { types, generateID } = this.options;
        const canSetId = types.includes(node.type.name) && node.attrs[UniqueIDAttributeName] === null;
        if (!canSetId) return false;
        tr.setNodeMarkup(pos, undefined, {
          ...node.attrs,
          [UniqueIDAttributeName]: generateID(),
        });
        tr.setMeta("addToHistory", false);
        return true;
      },
    };
  },

  addProseMirrorPlugins() {
    let dragSourceElement: HTMLElement | null = null;
    let transformPasted = false;
    return [
      new Plugin({
        key: UniqueIDPluginKey,
        appendTransaction: (transactions, oldState, newState) => {
          const docChanges = transactions.some(transaction => transaction.docChanged) && !oldState.doc.eq(newState.doc);
          let filterTransactions = false;
          if (this.options.filterTransaction) {
            const filter = this.options.filterTransaction;
            filterTransactions = transactions.some(tr => !filter(tr));
          }
          if (!docChanges || filterTransactions) {
            return;
          }

          const { tr } = newState;
          const selectedNodeId = isNodeSelection(tr.selection) ? getNodeUniqueId(tr.selection.node) : undefined;
          const { types, generateID } = this.options;
          const transform = combineTransactionSteps(oldState.doc, [...transactions]);

          const { mapping } = transform;
          // get changed ranges based on the old state
          const changes = getChangedRanges(transform);

          // compute and cache all existing unique ids for this transaction
          const allIds = (() => {
            let result: string[] | undefined;
            return () => {
              if (result) return result;
              result = changes.length
                ? findChildren(newState.doc, node => !!getNodeUniqueId(node))
                    .map(node => getNodeUniqueId(node.node)!)
                : [];

              return result;
            };
          })();

          changes.forEach(({ newRange }) => {
            const newNodes = findChildrenInRange(
              newState.doc,
              newRange,
              node => {
                return types.includes(node.type.name);
              },
            );
            const newIds = newNodes
              .map(({ node }) => node.attrs[UniqueIDAttributeName] as string)
              .filter(id => !!id);

            const duplicatedNewIds = findDuplicates(newIds);

            newNodes.forEach(({ node, pos }) => {
              let id;
              // instead of checking `node.attrs[attributeName]` directly
              // we look at the current state of the node within `tr.doc`.
              // this helps to prevent adding new ids to the same node
              // if the node changed multiple times within one transaction
              const nodeAtPos = tr.doc.nodeAt(pos);
              if (nodeAtPos) {
                id = getNodeUniqueId(nodeAtPos);
              }

              if (!id) {
                let existingId;
                // nodeAt will throw if position is outside of fragment
                try {
                  const previousNodeAtPos = oldState.doc.nodeAt(pos);
                  if (previousNodeAtPos) {
                    existingId = getNodeUniqueId(previousNodeAtPos);
                  }
                  if (existingId && !newIds.includes(existingId) && !allIds().includes(existingId)) {
                    tr.setNodeMarkup(pos, undefined, {
                      ...node.attrs,
                      [UniqueIDAttributeName]: existingId,
                    });

                    documentNodeAdded.trigger(node.type.name);
                    return;
                  }
                } catch {
                  // ignore
                }
              }

              // completely new node, set new id
              if (!id) {
                tr.setNodeMarkup(pos, undefined, {
                  ...node.attrs,
                  [UniqueIDAttributeName]: generateID(),
                });

                documentNodeAdded.trigger(node.type.name);
                return;
              }

              // check if the node doesn’t exist in the old state
              const { deleted } = mapping.invert().mapResult(pos);
              const newNode = deleted && duplicatedNewIds.includes(id);
              if (newNode) {
                // try to preserve previous NodeSelection
                // as tr.setNodeMarkup may clear it
                const previouslySelected = selectedNodeId && selectedNodeId === getNodeUniqueId(node);
                tr.setNodeMarkup(pos, undefined, {
                  ...node.attrs,
                  [UniqueIDAttributeName]: generateID(),
                });

                if (previouslySelected) {
                  tr.setSelection(NodeSelection.create(tr.doc, pos));
                }
              }
            });
          });
          if (!tr.steps.length) {
            return;
          }
          return tr;
        },
        // we register a global drag handler to track the current drag source element
        view(view) {
          const handleDragstart = (event: DragEvent) => {
            if (!(event.target instanceof Element)) return;
            const isInsideView = view.dom.parentElement && view.dom.parentElement.contains(event.target);
            dragSourceElement = isInsideView ? view.dom.parentElement : null;
          };
          window.addEventListener("dragstart", handleDragstart);
          return {
            destroy() {
              window.removeEventListener("dragstart", handleDragstart);
            },
          };
        },
        props: {
          // `handleDOMEvents` is called before `transformPasted`
          // so we can do some checks before
          handleDOMEvents: {
            // only create new ids for dropped content while holding `alt`
            // or content is dragged from another editor
            drop: (view, event) => {
              if (dragSourceElement === view.dom.parentElement) {
                return false;
              }

              const dataTransfer = event.dataTransfer;
              if (dataTransfer && dataTransfer.effectAllowed === "copy") {
                dragSourceElement = null;
                transformPasted = true;
              }
              return false;
            },
            // always create new ids on pasted content
            paste: () => {
              transformPasted = true;
              return false;
            },
          },
          // we’ll remove ids for every pasted node
          // so we can create a new one within `appendTransaction`
          transformPasted: slice => {
            if (!transformPasted) {
              return slice;
            }
            const { types } = this.options;
            const removeId = (fragment: Fragment) => {
              const list: PMNode[] = [];
              fragment.forEach(node => {
                // don’t touch text nodes
                if (node.isText) {
                  list.push(node);
                  return;
                }

                // check for any other child nodes
                if (!types.includes(node.type.name)) {
                  list.push(node.copy(removeId(node.content)));
                  return;
                }

                // remove id
                const nodeWithoutId = node.type.create(
                  {
                    ...node.attrs,
                    [UniqueIDAttributeName]: null,
                  },
                  removeId(node.content),
                  node.marks,
                );
                list.push(nodeWithoutId);
              });

              return Fragment.from(list);
            };
            // reset check
            transformPasted = false;
            const contentRemovedIds = removeId(slice.content);

            return new Slice(
              contentRemovedIds,
              slice.openStart,
              slice.openEnd,
            );
          },
        },
      }),
    ];
  },
});

export default UniqueID;
